import { useEffect, useMemo, useState } from 'react'
import {
  AlertTriangle,
  Clock3,
  Download,
  Eye,
  FileText,
  FlaskConical,
  MessageSquare,
  Pill,
  Stethoscope
} from 'lucide-react'
import toast from 'react-hot-toast'

import {
  buildConsultationSummaries,
  exportConsultationSummaryPdf,
  exportDoctorDossierPdf
} from '@/lib/consultation-summary'
import { formatMedicalDocumentDate } from '@/lib/medical-document-presentation'
import type {
  ActiveMedication,
  HealthEntry,
  LabResult,
  MedicalDocument,
  MedicationHistory
} from '@/types/health'

function CompactSection({
  title,
  icon: Icon,
  children
}: {
  title: string
  icon: typeof Stethoscope
  children: React.ReactNode
}) {
  return (
    <section className="rounded-2xl border border-dark-border bg-dark-surface-high p-4">
      <div className="mb-3 flex items-center gap-2">
        <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-dark-surface text-cyan-300">
          <Icon className="h-4 w-4" />
        </div>
        <h3 className="text-sm font-semibold text-white">{title}</h3>
      </div>
      {children}
    </section>
  )
}

export default function MedicalConsultationPanel({
  documents,
  labResults,
  healthEntries,
  activeMedications,
  medicationHistory,
  patientName,
  isLoading,
  onPreviewDocument
}: {
  documents: MedicalDocument[]
  labResults: LabResult[]
  healthEntries: HealthEntry[]
  activeMedications: ActiveMedication[]
  medicationHistory: MedicationHistory[]
  patientName?: string | null
  isLoading?: boolean
  onPreviewDocument: (document: MedicalDocument) => void
}) {
  const [selectedSpecialty, setSelectedSpecialty] = useState('')

  const summaries = useMemo(
    () =>
      buildConsultationSummaries({
        documents,
        labResults,
        healthEntries,
        activeMedications,
        medicationHistory
      }),
    [documents, labResults, healthEntries, activeMedications, medicationHistory]
  )

  useEffect(() => {
    if (!summaries.length) {
      setSelectedSpecialty('')
      return
    }

    if (!selectedSpecialty || !summaries.some((summary) => summary.specialty === selectedSpecialty)) {
      setSelectedSpecialty(summaries[0].specialty)
    }
  }, [selectedSpecialty, summaries])

  const selectedSummary =
    summaries.find((summary) => summary.specialty === selectedSpecialty) || summaries[0] || null
  const documentsById = useMemo(
    () => new Map(documents.map((document) => [document.id, document])),
    [documents]
  )

  function handleExportPdf() {
    if (!selectedSummary) {
      return
    }

    try {
      exportConsultationSummaryPdf({
        summary: selectedSummary,
        patientName
      })
      toast.success('PDF de consultation exporte')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Export PDF impossible')
    }
  }

  function handleExportDoctorDossier() {
    try {
      exportDoctorDossierPdf({
        patientName,
        summaries,
        documents,
        labResults,
        activeMedications,
        medicationHistory,
        healthEntries
      })
      toast.success('Dossier medecin exporte')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Export du dossier medecin impossible')
    }
  }

  if (isLoading) {
    return (
      <div className="card py-8 text-center">
        <div className="spinner mx-auto" />
        <p className="mt-4 text-sm text-slate-400">Preparation du mode consultation...</p>
      </div>
    )
  }

  if (!selectedSummary) {
    return (
      <div className="card py-10 text-center">
        <Stethoscope className="mx-auto h-10 w-10 text-slate-500" />
        <h2 className="mt-4 text-lg font-semibold text-white">Mode consultation</h2>
        <p className="mx-auto mt-2 max-w-lg text-sm text-slate-400">
          Importez quelques documents ou relancez l analyse IA pour generer une fiche de rendez-vous
          exploitable tout de suite.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="card space-y-4">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-lg font-semibold text-white">{selectedSummary.headline}</h2>
              {selectedSummary.focus_label && (
                <span className="rounded-full border border-cyan-500/20 bg-cyan-500/10 px-2.5 py-1 text-xs text-cyan-100">
                  {selectedSummary.focus_label}
                </span>
              )}
            </div>
            <p className="mt-2 max-w-3xl text-sm text-slate-300">{selectedSummary.quick_summary}</p>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row">
            <select
              value={selectedSpecialty}
              onChange={(event) => setSelectedSpecialty(event.target.value)}
              className="input min-w-[220px]"
            >
              {summaries.map((summary) => (
                <option key={summary.specialty} value={summary.specialty}>
                  {summary.specialty}
                </option>
              ))}
            </select>

            <button type="button" onClick={handleExportPdf} className="btn-secondary">
              <Download className="h-4 w-4" />
              Exporter PDF
            </button>

            <button type="button" onClick={handleExportDoctorDossier} className="btn-secondary">
              <Download className="h-4 w-4" />
              Dossier medecin
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <CompactSection title="Points a retenir" icon={AlertTriangle}>
          <div className="space-y-2">
            {selectedSummary.key_points.length > 0 ? (
              selectedSummary.key_points.map((item) => (
                <p key={item} className="text-sm text-slate-300">
                  {item}
                </p>
              ))
            ) : (
              <p className="text-sm text-slate-500">Aucun point cle supplementaire.</p>
            )}
          </div>
        </CompactSection>

        <CompactSection title="Traitements actifs" icon={Pill}>
          <div className="space-y-2">
            {selectedSummary.active_treatments.length > 0 ? (
              selectedSummary.active_treatments.map((item) => (
                <p key={item} className="text-sm text-slate-300">
                  {item}
                </p>
              ))
            ) : (
              <p className="text-sm text-slate-500">Aucun traitement actif retrouve.</p>
            )}
          </div>
        </CompactSection>

        <CompactSection title="Biologie recente" icon={FlaskConical}>
          <div className="space-y-3">
            {selectedSummary.abnormal_biology.length > 0 ? (
              selectedSummary.abnormal_biology.map((item) => {
                const document = item.document_id ? documentsById.get(item.document_id) : null

                return (
                  <div
                    key={item.id}
                    className="flex items-start justify-between gap-3 rounded-xl border border-dark-border bg-dark-surface p-3"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-white">{item.test_name}</p>
                      <p className="mt-1 text-sm text-slate-300">
                        {item.value_label} le {formatMedicalDocumentDate(item.date)}
                      </p>
                      {item.unit_warning && (
                        <p className="mt-1 text-xs text-amber-300">{item.unit_warning}</p>
                      )}
                    </div>

                    {document && (
                      <button
                        type="button"
                        onClick={() => onPreviewDocument(document)}
                        className="rounded-lg border border-dark-border bg-dark-surface-high p-2 text-slate-300 transition-colors hover:text-white"
                        title="Ouvrir le document source"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                )
              })
            ) : (
              <p className="text-sm text-slate-500">Pas d anomalie biologique recente visible pour cette vue.</p>
            )}
          </div>
        </CompactSection>

        <CompactSection title="Questions du rendez-vous" icon={MessageSquare}>
          <div className="space-y-2">
            {selectedSummary.questions.length > 0 ? (
              selectedSummary.questions.map((item) => (
                <p key={item} className="text-sm text-slate-300">
                  {item}
                </p>
              ))
            ) : (
              <p className="text-sm text-slate-500">Aucune question pre-remplie.</p>
            )}
          </div>
        </CompactSection>

        <CompactSection title="Documents cles" icon={FileText}>
          <div className="space-y-3">
            {selectedSummary.documents.length > 0 ? (
              selectedSummary.documents.map((item) => {
                const document = documentsById.get(item.id)

                return (
                  <div
                    key={item.id}
                    className="flex items-start justify-between gap-3 rounded-xl border border-dark-border bg-dark-surface p-3"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-white">{item.title}</p>
                      <p className="mt-1 text-xs text-slate-400">{item.subtitle}</p>
                      <p className="mt-2 text-sm text-slate-300">{item.summary}</p>
                    </div>

                    {document && (
                      <button
                        type="button"
                        onClick={() => onPreviewDocument(document)}
                        className="rounded-lg border border-dark-border bg-dark-surface-high p-2 text-slate-300 transition-colors hover:text-white"
                        title="Apercu du PDF"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                )
              })
            ) : (
              <p className="text-sm text-slate-500">Aucun document cle pour cette specialite.</p>
            )}
          </div>
        </CompactSection>

        <CompactSection title="Chronologie courte" icon={Clock3}>
          <div className="space-y-3">
            {selectedSummary.timeline.length > 0 ? (
              selectedSummary.timeline.map((item) => (
                <div key={item.id} className="rounded-xl border border-dark-border bg-dark-surface p-3">
                  <p className="text-xs uppercase tracking-[0.16em] text-slate-500">
                    {formatMedicalDocumentDate(item.date)}
                  </p>
                  <p className="mt-1 text-sm font-medium text-white">{item.label}</p>
                  <p className="mt-1 text-sm text-slate-300">{item.detail}</p>
                </div>
              ))
            ) : (
              <p className="text-sm text-slate-500">Chronologie non disponible.</p>
            )}
          </div>
        </CompactSection>
      </div>
    </div>
  )
}
